import React, { memo, useCallback, useMemo } from 'react'
import { Form, Formik } from 'formik'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import { InsurancePolicyProductFormContent } from './InsurancePolicyProductFormContent'
import { buildInsurancePolicyProductValidator } from './InsurancePolicyProductForm.validator'
import { toInsurancePolicyProductFormValues } from './InsurancePolicyProductForm.mapper'
import type {
  InsurancePolicyProductFormMode,
  InsurancePolicyProductFormValues,
} from './InsurancePolicyProductForm.interface'

interface Props {
  formId: string
  mode: InsurancePolicyProductFormMode
  policyProduct: InsurancePolicyProduct | null
  selectableProducts: InsuranceProduct[]
  docusignTemplates: DocusignTemplate[]
  onSubmit(values: InsurancePolicyProductFormValues): void
}

const InsurancePolicyProductFormComponent = ({
  formId,
  mode,
  policyProduct,
  selectableProducts,
  docusignTemplates,
  onSubmit,
}: Props) => {
  const initialValues = useMemo<InsurancePolicyProductFormValues>(() => {
    return toInsurancePolicyProductFormValues(policyProduct)
  }, [policyProduct])

  const validationSchema = useMemo(() => {
    return buildInsurancePolicyProductValidator(mode)
  }, [mode])

  // The catalog row may be missing if it was deleted from the global catalog after assignment;
  // fall back to the raw id rather than inventing a name.
  const lockedProductName =
    policyProduct?.insuranceProduct?.name ?? policyProduct?.insuranceProductId ?? ''

  const handleSubmit = useCallback(
    (values: InsurancePolicyProductFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<InsurancePolicyProductFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      // Reopening the modal on a different row must reseed every field.
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form id={formId}>
        <InsurancePolicyProductFormContent
          mode={mode}
          selectableProducts={selectableProducts}
          docusignTemplates={docusignTemplates}
          lockedProductName={lockedProductName}
          lockedFeeFrequency={policyProduct?.feeFrequency ?? ''}
        />
      </Form>
    </Formik>
  )
}

export const InsurancePolicyProductForm = memo(InsurancePolicyProductFormComponent)
